/*
 * klaviyo-popup-fix.js
 *
 * Klaviyo's signup forms inject markup at runtime with obfuscated
 * class names that change between SDK builds (e.g.
 * .kl-private-reset-css-Xuajs1, .go123abc). Pure-CSS overrides
 * miss the close button on those forms and the user sees a blue
 * circular focus / accent ring with an invisible X glyph.
 *
 * This script bypasses the CSS-selector race entirely:
 *  1. Listens for the `klaviyoForms` event the SDK fires on open.
 *  2. Also runs a MutationObserver as a fallback for older SDK
 *     builds that do not fire that event.
 *  3. Scans every container that looks like a Klaviyo form
 *     ([class*="klaviyo"], [class*="kl-"], [class*="kl_"],
 *     [id*="klaviyo"]) and identifies the close button by its
 *     structural signature (small button positioned near a corner,
 *     contains an X glyph or has an aria-label that mentions
 *     close/dismiss/X).
 *  4. Applies inline styles directly so nothing in the cascade can
 *     overrule it.
 */
(function () {
  'use strict';

  var FORM_SELECTOR =
    '[class*="klaviyo"], [class*="kl-" i], [class*="kl_" i], [id*="klaviyo" i]';

  // Keys are CSS property names (kebab-case) so setProperty() works
  // for vendor-prefixed properties like -webkit-appearance.
  var CLOSE_BUTTON_STYLES = {
    'background': 'transparent',
    'background-color': 'transparent',
    'background-image': 'none',
    'border': '0',
    'border-radius': '0',
    'box-shadow': 'none',
    'outline': '0',
    '-webkit-appearance': 'none',
    'appearance': 'none',
    'accent-color': '#232323',
    'color': '#232323',
    'padding': '6px',
    'min-width': '0',
    'width': 'auto',
    'height': 'auto',
    'opacity': '1',
    'visibility': 'visible'
  };

  var GLYPH_STYLES = {
    'fill': '#232323',
    'stroke': '#232323',
    'color': '#232323',
    'opacity': '1',
    'visibility': 'visible'
  };

  function applyStyles(el, styles) {
    if (!el || !el.style || typeof el.style.setProperty !== 'function') return;
    Object.keys(styles).forEach(function (prop) {
      try {
        el.style.setProperty(prop, styles[prop], 'important');
      } catch (e) { /* noop */ }
    });
  }

  function looksLikeCloseButton(btn) {
    if (btn && btn.closest && btn.closest('#bb2d-stage, .bb2d, .bb2d-overlays')) return false;
    if (!btn || btn.dataset.kFixed === '1') return false;
    if (btn.type === 'submit') return false;

    var text = (btn.textContent || '').trim();
    var label = (btn.getAttribute('aria-label') || '').toLowerCase();
    var className = btn.className ? btn.className.toString().toLowerCase() : '';

    // Any button that carries meaningful text content (e.g. SIGN UP,
    // SUBSCRIBE, JOIN) is NEVER the close button. We bail early to
    // avoid stripping the primary CTA's styling.
    var hasOnlyXText = text === '×' || text === 'x' || text === 'X' || text === '✕';
    if (text && !hasOnlyXText) return false;

    if (/close|dismiss|exit|cancel/.test(label)) return true;
    if (className.indexOf('close') !== -1 || className.indexOf('dismiss') !== -1) return true;
    if (hasOnlyXText) return true;

    return false;
  }

  function fixButton(btn) {
    if (!btn || btn.dataset.kFixed === '1') return;
    applyStyles(btn, CLOSE_BUTTON_STYLES);

    // Paint the glyph (SVG or text) in dark.
    var glyphs = btn.querySelectorAll('svg, svg *, span, i, path, line, polygon, circle, rect');
    for (var i = 0; i < glyphs.length; i++) {
      applyStyles(glyphs[i], GLYPH_STYLES);
    }

    // If there is no visible glyph, paint our own X via inline style.
    var hasGlyph = btn.querySelector('svg, span, i') || (btn.textContent || '').trim();
    if (!hasGlyph) {
      btn.textContent = '✕'; // unicode X
      btn.style.setProperty('font-size', '18px', 'important');
      btn.style.setProperty('line-height', '1', 'important');
      btn.style.setProperty('font-weight', '400', 'important');
    }

    btn.dataset.kFixed = '1';
  }

  // Visual-detection pass: identifies the broken close affordance
  // by its computed styles (visible border + roughly circular shape +
  // no text content). This catches it regardless of tag, role, class,
  // or aria-label - which is the only reliable thing we have left
  // after the targeted selectors miss.
  function isBrokenCloseAffordance(el) {
    if (!el || el.dataset.kFixed === '1') return false;
    // NEVER touch the 2D bracelet builder. Its empty-state placeholder beads
    // are small bordered circles with no text, which is exactly the shape this
    // pass hunts for, so it was writing a big X into every one of them and the
    // bracelet loaded as a ring of crosses. The body carries a class the form
    // selector matches, so this whole page is scanned.
    if (el.closest && el.closest('#bb2d-stage, .bb2d, .bb2d-overlays')) return false;
    var tag = el.tagName;
    if (!tag) return false;
    // Skip form controls that obviously aren't a close button.
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' ||
        tag === 'FORM' || tag === 'LABEL' || tag === 'P' ||
        tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4' ||
        tag === 'IMG' || tag === 'PICTURE') return false;
    // Must have no text content (the X is invisible or not text).
    var text = (el.textContent || '').trim();
    if (text && text !== '×' && text.toLowerCase() !== 'x' && text !== '✕') return false;
    // Skip if it has substantial children that look like content
    // (we want a leaf-ish element).
    if (el.children && el.children.length > 3) return false;

    var rect = el.getBoundingClientRect();
    if (rect.width < 12 || rect.width > 80) return false;
    if (rect.height < 12 || rect.height > 80) return false;
    var ratio = rect.width / rect.height;
    if (ratio < 0.7 || ratio > 1.4) return false;

    var cs = window.getComputedStyle(el);
    // Must have a visible border ...
    var borderColor = cs.borderTopColor || cs.borderColor || '';
    var borderWidth = parseFloat(cs.borderTopWidth) || 0;
    var hasVisibleBorder =
      borderWidth >= 1 &&
      borderColor &&
      borderColor.indexOf('rgba(0, 0, 0, 0)') === -1 &&
      borderColor !== 'transparent';
    // ... AND a >=40% border-radius (i.e. round or pill).
    var radiusPx = parseFloat(cs.borderTopLeftRadius) || 0;
    var radiusPct = (radiusPx / Math.max(rect.width, 1)) * 100;
    var isRound = radiusPct >= 40 ||
      cs.borderTopLeftRadius.indexOf('%') !== -1 && parseFloat(cs.borderTopLeftRadius) >= 40;

    return hasVisibleBorder && isRound;
  }

  function scanRoot(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    var forms;
    try {
      if (root.matches && root.matches(FORM_SELECTOR)) {
        forms = [root];
      } else {
        forms = root.querySelectorAll(FORM_SELECTOR);
      }
    } catch (e) {
      return;
    }
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];

      // Pass 1: targeted close-button candidates by class / aria-label.
      var candidates = form.querySelectorAll('button, [role="button"]');
      for (var j = 0; j < candidates.length; j++) {
        if (looksLikeCloseButton(candidates[j])) {
          fixButton(candidates[j]);
        }
      }

      // Pass 2: visual detection - anything that paints as a small
      // circle with a visible border and no text content.
      var all = form.querySelectorAll('*');
      for (var k = 0; k < all.length; k++) {
        if (isBrokenCloseAffordance(all[k])) {
          fixButton(all[k]);
        }
      }
    }
  }

  // Klaviyo mounts a full-viewport position:fixed container that keeps
  // pointer-events:auto even when nothing visible fills it, so every tap
  // on the page (product photos included) dies on an invisible layer.
  // Make the big transparent layer click-through and re-enable pointer
  // events only on its real interactive content. Runs on the same hooks
  // as the close-button fix, plus a slow interval because Klaviyo
  // re-injects the layer continuously.
  function unblockOverlays() {
    var layers = document.querySelectorAll('[class*="kl-private-reset-css"]');
    for (var i = 0; i < layers.length; i++) {
      var el = layers[i];
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed') continue;
      var r = el.getBoundingClientRect();
      if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.9) continue;
      // the layer itself is transparent - only its children paint
      var bg = cs.backgroundColor;
      var transparent = !bg || bg === 'transparent' || bg.indexOf('rgba(0, 0, 0, 0)') !== -1;
      if (!transparent) continue;
      el.style.setProperty('pointer-events', 'none', 'important');
      var kids = el.querySelectorAll('form, [role="dialog"], [data-testid], button, a, input, select, textarea');
      for (var j = 0; j < kids.length; j++) {
        kids[j].style.setProperty('pointer-events', 'auto', 'important');
      }
    }
  }

  function scanAll() {
    scanRoot(document.body);
    unblockOverlays();
  }

  setInterval(unblockOverlays, 1500);

  // 1) SDK event hook
  window.addEventListener('klaviyoForms', function (e) {
    if (!e || !e.detail) return;
    if (e.detail.type === 'open' || e.detail.type === 'embedOpen') {
      // Klaviyo paints the close button a tick after the event fires.
      setTimeout(scanAll, 0);
      setTimeout(scanAll, 50);
      setTimeout(scanAll, 250);
      setTimeout(scanAll, 750);
    }
  });

  // 2) MutationObserver fallback for SDK builds that do not emit the
  //    klaviyoForms event reliably.
  function startObserver() {
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scanRoot(added[j]);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scanAll();
      startObserver();
    });
  } else {
    scanAll();
    startObserver();
  }
})();
